'use client';

import { useMemo, useState, useEffect, useCallback } from 'react';
import { EndpointStatus, EndpointResult } from './types';
import { fetchEndpointStatuses } from './nodeStatusApi';
import EndpointCard from './EndpointCard';
import Tooltip from './Tooltip';

interface GroupRowProps {
  group: string;
  endpoints: EndpointStatus[];
  expanded: boolean;
  onToggle: () => void;
  showAvg: boolean;
  onTooltip: (r: EndpointResult | null, x: number, y: number, p: boolean) => void;
}

function GroupRow({ group, endpoints, expanded, onToggle, showAvg, onTooltip }: GroupRowProps) {
  const unhealthy = endpoints.filter((e) => !(e.results[e.results.length - 1]?.success ?? true)).length;
  const allOk = unhealthy === 0;

  return (
    <div className="rounded-xl border border-white/10 overflow-hidden bg-bg-card">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 bg-transparent border-b border-white/10 cursor-pointer hover:bg-white/5 transition-colors"
      >
        <div className="flex items-center gap-3">
          <svg
            className={`w-5 h-5 text-text-muted transition-transform duration-200 ${expanded ? 'rotate-0' : '-rotate-90'}`}
            fill="none" stroke="currentColor" viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
          <h2 className="text-base font-semibold text-white">{group}</h2>
        </div>
        {allOk ? (
          <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        ) : (
          <span className="bg-red-500 text-white text-xs font-semibold px-2 py-0.5 rounded-full">
            {unhealthy}
          </span>
        )}
      </button>

      {expanded && (
        <div className="p-4">
          <div className="flex flex-col gap-3">
            {endpoints.map((ep) => (
              <EndpointCard
                key={ep.key}
                endpoint={ep}
                showAvgResponseTime={showAvg}
                onTooltip={onTooltip}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function NodeStatusDashboard() {
  const [endpoints, setEndpoints] = useState<EndpointStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const [search, setSearch] = useState('');
  const [showOnlyFailing, setShowOnlyFailing] = useState(false);
  const [showRecentFailures, setShowRecentFailures] = useState(false);
  const [groupByGroup, setGroupByGroup] = useState(true);
  const [showAvg, setShowAvg] = useState(true);

  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  useEffect(() => {
    try {
      setShowAvg(localStorage.getItem('ns:show-avg') !== 'false');
      const saved = localStorage.getItem('ns:uncollapsed-groups');
      if (saved) setExpandedGroups(new Set(JSON.parse(saved)));
    } catch {}
  }, []);

  const toggleGroup = useCallback((group: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(group)) next.delete(group); else next.add(group);
      try { localStorage.setItem('ns:uncollapsed-groups', JSON.stringify(Array.from(next))); } catch {}
      return next;
    });
  }, []);

  const [tooltipResult, setTooltipResult] = useState<EndpointResult | null>(null);
  const [tooltipX, setTooltipX] = useState(0);
  const [tooltipY, setTooltipY] = useState(0);

  const handleTooltip = useCallback((result: EndpointResult | null, x: number, y: number, _p: boolean) => {
    setTooltipResult(result);
    setTooltipX(x);
    setTooltipY(y);
  }, []);

  const load = useCallback(async (forceReset = false) => {
    if (forceReset) setEndpoints([]);
    setError(null);
    try {
      const statuses = await fetchEndpointStatuses();
      setEndpoints(statuses || []);
      setLastUpdated(new Date());
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(() => load(), 5 * 60 * 1000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    try { localStorage.setItem('ns:show-avg', showAvg ? 'true' : 'false'); } catch {}
  }, [showAvg]);

  const filtered = useMemo(() => {
    let list = endpoints;
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((e) => e.name.toLowerCase().includes(q) || e.group.toLowerCase().includes(q));
    }
    if (showOnlyFailing) list = list.filter((e) => !(e.results[e.results.length - 1]?.success ?? true));
    if (showRecentFailures) list = list.filter((e) => e.results.some((r) => !r.success));
    return [...list].sort((a, b) => a.name.localeCompare(b.name));
  }, [endpoints, search, showOnlyFailing, showRecentFailures]);

  const groups = useMemo(() => {
    const map = new Map<string, EndpointStatus[]>();
    for (const ep of filtered) {
      const g = ep.group || 'No Group';
      if (!map.has(g)) map.set(g, []);
      map.get(g)!.push(ep);
    }
    return Array.from(map.entries()).sort(([a], [b]) => {
      if (a === 'No Group') return 1;
      if (b === 'No Group') return -1;
      return a.localeCompare(b);
    });
  }, [filtered]);

  return (
    <div className="relative">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 gap-2">
        <div className="min-w-0">
          <h2 className="text-xl font-semibold text-white">Node Status</h2>
          <p className="text-xs text-text-muted mt-0.5">Live health of all blockchain nodes</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {lastUpdated && (
            <span className="text-xs text-text-muted hidden md:block">
              Updated {lastUpdated.toLocaleTimeString()}
            </span>
          )}
          <button
            onClick={() => setShowAvg((v) => !v)}
            title={showAvg ? 'Switch to min-max' : 'Switch to average'}
            className="hidden sm:flex p-1.5 rounded-md border border-white/10 hover:bg-white/5 text-text-muted transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </button>
          <button
            onClick={() => load(true)}
            disabled={loading}
            className="flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-medium text-text-secondary hover:bg-white/10 transition-colors disabled:opacity-50"
          >
            <svg className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            <span className="hidden sm:inline">Refresh</span>
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-400 mb-6">
          ⚠️ {error}{' · '}
          <button onClick={() => load()} className="underline font-medium">
            retry
          </button>
        </div>
      )}

      {/* Toolbar */}
      <div className="flex flex-col gap-3 mb-5">
        {/* Search — full width on all screens */}
        <div className="relative w-full">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            placeholder="Search nodes..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-lg border border-white/10 bg-white/5 pl-9 pr-3 py-2 text-sm text-white placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent/50"
          />
        </div>
        {/* Filters row */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <label className="flex items-center gap-1.5 text-xs text-text-secondary cursor-pointer select-none">
            <input
              type="checkbox"
              checked={showOnlyFailing}
              onChange={(e) => setShowOnlyFailing(e.target.checked)}
              className="rounded border-white/20 bg-white/5"
            />
            Only failing
          </label>
          <label className="flex items-center gap-1.5 text-xs text-text-secondary cursor-pointer select-none">
            <input
              type="checkbox"
              checked={showRecentFailures}
              onChange={(e) => setShowRecentFailures(e.target.checked)}
              className="rounded border-white/20 bg-white/5"
            />
            Recent failures
          </label>
          <label className="flex items-center gap-1.5 text-xs text-text-secondary cursor-pointer select-none">
            <input
              type="checkbox"
              checked={groupByGroup}
              onChange={(e) => setGroupByGroup(e.target.checked)}
              className="rounded border-white/20 bg-white/5"
            />
            Group by chain
          </label>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="space-y-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="rounded-xl border border-white/10 bg-bg-card animate-pulse">
              <div className="flex items-center justify-between px-4 py-3">
                <div className="flex items-center gap-3">
                  <div className="w-5 h-5 bg-white/10 rounded" />
                  <div className="h-4 bg-white/10 rounded w-28" />
                </div>
                <div className="w-5 h-5 bg-white/10 rounded-full" />
              </div>
            </div>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-sm text-text-muted">No nodes match your filters.</div>
      ) : groupByGroup ? (
        <div className="space-y-3">
          {groups.map(([g, eps]) => (
            <GroupRow
              key={g}
              group={g}
              endpoints={eps}
              expanded={expandedGroups.has(g)}
              onToggle={() => toggleGroup(g)}
              showAvg={showAvg}
              onTooltip={handleTooltip}
            />
          ))}
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {filtered.map((ep) => (
            <EndpointCard key={ep.key} endpoint={ep} showAvgResponseTime={showAvg} onTooltip={handleTooltip} />
          ))}
        </div>
      )}

      <Tooltip result={tooltipResult} cursorX={tooltipX} cursorY={tooltipY} />
    </div>
  );
}
